import { useRef, useState } from 'react'

export function usePomodoro(
	initialTimeLeft: number,
	callbacks: {
		finish?: VoidFunction
		start?: VoidFunction
		pause?: VoidFunction
		reset?: VoidFunction
	} = {
		finish: () => {},
		start: () => {},
		pause: () => {},
		reset: () => {},
	},
) {
	const [timeLeft, setTimeLeft] = useState<number>(initialTimeLeft)
	const [status, setStatus] = useState<'idle' | 'running' | 'paused'>('idle')
	const startTimeRef = useRef<number>(0)
	const initialTimeRef = useRef<number>(initialTimeLeft)
	const intervalRef = useRef<ReturnType<typeof setInterval>>()

	const finishCallbackRef = useRef(callbacks.finish || (() => {}))
	const startCallbackRef = useRef(callbacks.start || (() => {}))
	const pauseCallbackRef = useRef(callbacks.pause || (() => {}))
	const resetCallbackRef = useRef(callbacks.reset || (() => {}))
	finishCallbackRef.current = callbacks.finish || (() => {})
	startCallbackRef.current = callbacks.start || (() => {})
	pauseCallbackRef.current = callbacks.pause || (() => {})
	resetCallbackRef.current = callbacks.reset || (() => {})

	function start() {
		if (intervalRef.current) {
			return
		}

		const currentTime = Date.now()
		startTimeRef.current = currentTime
		setTimeLeft(initialTimeRef.current)
		setStatus('running')
		startCallbackRef.current()

		intervalRef.current = setInterval(() => {
			if (initialTimeRef.current - (Date.now() - startTimeRef.current) <= 0) {
				clearInterval(intervalRef.current)
				intervalRef.current = undefined
				setTimeLeft(0)
				setStatus('idle')
				finishCallbackRef.current()
				return
			}
			setTimeLeft(initialTimeRef.current - (Date.now() - startTimeRef.current))
		}, 500)
	}

	function reset() {
		initialTimeRef.current = initialTimeLeft
		setTimeLeft(initialTimeLeft)
		setStatus('idle')
		resetCallbackRef.current()
		startTimeRef.current = Date.now()
		clearInterval(intervalRef.current)
		intervalRef.current = undefined
	}

	function pause() {
		if (intervalRef.current === undefined) {
			return
		}

		setStatus('paused')
		pauseCallbackRef.current()
		initialTimeRef.current = timeLeft
		clearInterval(intervalRef.current)
		intervalRef.current = undefined
	}

	return { start, pause, reset, timeLeft, status }
}
